import { useState, useEffect, useCallback } from 'react';
import posSettingsService from '../../../../../services/posSettingsService';

export const usePOSSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // ✅ ENHANCED: Default fallback settings structure
  const getDefaultSettings = useCallback(() => ({
    cartAddOns: [
      {
        id: 'service_charge',
        name: 'Service Charge',
        type: 'percentage',
        value: 10,
        enabled: true,
        visible: true,
        order: 1,
        editable: true
      },
      {
        id: 'gst',
        name: 'GST',
        type: 'percentage',
        value: 9,
        enabled: true,
        visible: true,
        order: 2,
        editable: true
      },
      {
        id: 'discount',
        name: 'Discount',
        type: 'discount',
        value: 0,
        enabled: false,
        visible: true,
        order: 3,
        editable: true
      }
    ],
    cartMetadata: {
      deviceNumber: {
        enabled: true,
        visible: true,
        required: false,
        label: 'Device Number',
        placeholder: 'Enter device number...'
      },
      tableNumber: {
        enabled: false,
        visible: false,
        required: false,
        label: 'Table Number',
        placeholder: 'Enter table number...'
      },
      customerName: {
        enabled: false,
        visible: false,
        required: false,
        label: 'Customer Name',
        placeholder: 'Enter customer name...'
      },
      notes: {
        enabled: false,
        visible: false,
        required: false,
        label: 'Order Notes',
        placeholder: 'Special instructions...'
      }
    },
    paymentMethods: {
      exactCash: {
        id: 'exact_cash',
        name: 'Exact Cash',
        enabled: true,
        visible: true,
        icon: 'MonetizationOn',
        order: 1
      },
      calculateChange: {
        id: 'calculate_change',
        name: 'Calculate Change',
        enabled: true,
        visible: true,
        icon: 'Calculate',
        order: 2
      },
      qrCode: {
        id: 'qr_code',
        name: 'QR Payment',
        enabled: false,
        visible: true,
        icon: 'QrCode',
        order: 3,
        comingSoon: true
      },
      creditCard: {
        id: 'credit_card',
        name: 'Credit Card',
        enabled: false,
        visible: true,
        icon: 'CreditCard',
        order: 4,
        comingSoon: true
      }
    },
    defaultSettings: {
      currency: 'USD',
      currencySymbol: '$',
      decimalPlaces: 2,
      autoCalculateTax: true,
      showItemTotals: true,
      allowNegativeQuantity: false,
      roundingMethod: 'round',
      defaultPaymentMethod: 'exact_cash'
    },
    _metadata: {
      stored_in_database: false,
      fallback_defaults: true
    }
  }), []);

  // ✅ FIXED: Load settings function with proper error handling
  const loadSettings = useCallback(async () => {
    console.log('📋 Loading POS settings...');
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await posSettingsService.getSettings();
      console.log('✅ Raw settings loaded:', data);
      
      if (!data || typeof data !== 'object') {
        console.warn('⚠️ Invalid settings data, using defaults');
        const defaultSettings = getDefaultSettings();
        setSettings(defaultSettings);
        setHasChanges(false);
        return;
      }

      // ✅ FIXED: Ensure cartAddOns is always an array
      let processedCartAddOns = getDefaultSettings().cartAddOns;
      
      if (data.cartAddOns) {
        if (Array.isArray(data.cartAddOns)) {
          processedCartAddOns = data.cartAddOns;
        } else if (typeof data.cartAddOns === 'object') {
          // Convert object to array
          const addOnValues = Object.values(data.cartAddOns);
          processedCartAddOns = addOnValues.filter(item => 
            item && typeof item === 'object' && item.id
          );
          
          if (processedCartAddOns.length === 0) {
            console.warn('⚠️ No valid add-ons found in object, using defaults');
            processedCartAddOns = getDefaultSettings().cartAddOns;
          }
        }
      }

      // ✅ Build final settings with guaranteed array for cartAddOns
      const finalSettings = {
        ...getDefaultSettings(),
        ...data,
        cartAddOns: processedCartAddOns, // Always an array
      };

      setSettings(finalSettings);
      setHasChanges(false);
      
      console.log('✅ Settings loaded successfully:', {
        totalKeys: Object.keys(finalSettings).length,
        cartAddOnsCount: finalSettings.cartAddOns.length,
        cartAddOnsIsArray: Array.isArray(finalSettings.cartAddOns)
      });
      
    } catch (error) {
      console.error('❌ Failed to load POS settings:', error);
      setError(error.message || 'Failed to load settings');
      
      // ✅ FIXED: Always provide fallback settings even on error
      const defaultSettings = getDefaultSettings();
      setSettings(defaultSettings);
      setHasChanges(false);
      
    } finally {
      setLoading(false);
    }
  }, [getDefaultSettings]);

  // ✅ FIXED: Update settings with proper error handling
  const updateSettings = useCallback(async (newSettings) => {
    console.log('🔄 Updating settings with:', newSettings);
    
    try {
      // ✅ Validate input
      if (!newSettings || typeof newSettings !== 'object') {
        throw new Error('Invalid settings format');
      }

      if (!settings) {
        throw new Error('Current settings not loaded');
      }

      // ✅ Merge settings properly
      const updatedSettings = {
        ...settings,
        ...newSettings
      };

      // ✅ Update local state immediately
      setSettings(updatedSettings);
      
      // ✅ Send to backend
      const result = await posSettingsService.updateSettings(updatedSettings);
      
      if (result?.settings) {
        setSettings(result.settings);
        
        // ✅ Emit event for other components
        window.dispatchEvent(new CustomEvent('pos-settings-updated', {
          detail: result.settings
        }));
      }
      
      console.log('✅ Settings updated successfully');
      return result;
      
    } catch (error) {
      console.error('❌ Failed to update settings:', error);
      // ✅ Reload settings on error to ensure consistency
      await loadSettings();
      throw error;
    }
  }, [settings, loadSettings]);

  // ✅ Manual save function
  const saveSettings = useCallback(async (settingsToSave = settings) => {
    console.log('💾 Manual save settings:', settingsToSave);
    
    try {
      setSaving(true);
      
      if (!settingsToSave) {
        throw new Error('No settings to save');
      }

      const result = await posSettingsService.saveSettings(settingsToSave);
      
      if (result?.settings) {
        setSettings(result.settings);
      }
      
      console.log('✅ Settings saved successfully');
      return result;
    } catch (error) {
      console.error('❌ Failed to save settings:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [settings]);

  // ✅ Reset to defaults
  const resetSettings = useCallback(async () => {
    try {
      setLoading(true);
      await posSettingsService.resetSettings();
      await loadSettings();
    } catch (error) {
      console.error('❌ Failed to reset settings:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [loadSettings]);

  // ✅ FIXED: Simplified refresh function
  const refreshSettings = useCallback(async () => {
    console.log('🔄 Refreshing settings...');
    await loadSettings();
  }, [loadSettings]);

  // ✅ Helper function to get cartAddOns as array
  const getCartAddOnsAsArray = useCallback(() => {
    if (!settings?.cartAddOns) {
      return [];
    }
    
    if (Array.isArray(settings.cartAddOns)) {
      return settings.cartAddOns;
    }
    
    if (typeof settings.cartAddOns === 'object') {
      return Object.values(settings.cartAddOns);
    }
    
    return [];
  }, [settings]);

  // ✅ Cart totals calculation
  const calculateCartTotals = useCallback((subtotal, userAddOnStates = {}) => {
    try {
      const cartAddOnsArray = getCartAddOnsAsArray();
      
      if (cartAddOnsArray.length === 0) {
        console.warn('⚠️ No cart add-ons available for calculation');
        return {
          subtotal: parseFloat(subtotal) || 0,
          addOns: {},
          total: parseFloat(subtotal) || 0
        };
      }

      let runningTotal = parseFloat(subtotal) || 0;
      const appliedAddOns = {};

      cartAddOnsArray.forEach(addOn => {
        const isEnabledInConfig = addOn.enabled && addOn.visible;
        const isCheckedByUser = userAddOnStates[addOn.id];
        
        if (isEnabledInConfig && isCheckedByUser) {
          let addOnAmount = 0;
          
          if (addOn.type === 'percentage') {
            addOnAmount = (runningTotal * parseFloat(addOn.value)) / 100;
          } else if (addOn.type === 'fixed') {
            addOnAmount = parseFloat(addOn.value);
          } else if (addOn.type === 'discount') {
            if (addOn.value > 0) {
              addOnAmount = -(runningTotal * parseFloat(addOn.value)) / 100;
            }
          }

          appliedAddOns[addOn.id] = addOnAmount;
          runningTotal += addOnAmount;
        }
      });

      const result = {
        subtotal: parseFloat(subtotal) || 0,
        addOns: appliedAddOns,
        total: Math.max(0, runningTotal)
      };

      return result;
      
    } catch (error) {
      console.error('❌ Error calculating cart totals:', error);
      return {
        subtotal: parseFloat(subtotal) || 0,
        addOns: {},
        total: parseFloat(subtotal) || 0
      };
    }
  }, [getCartAddOnsAsArray]);

  // ✅ FIXED: Load settings on mount with proper dependency
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    loading,
    error,
    saving,
    hasChanges,
    loadSettings,
    updateSettings,
    saveSettings,
    resetSettings,
    refreshSettings,
    calculateCartTotals,
    getCartAddOnsAsArray
  };
};